// features/cart/components/cart-content.tsx
"use client"

import * as React from "react"
import { useCallback, useEffect } from "react"
import Image from "next/image"
import { useParams, useRouter } from "next/navigation"
import { useQuery } from "@tanstack/react-query"
import {
  AlertCircle,
  ChevronRight,
  Clock,
  Edit2,
  ShoppingBag,
  Trash2,
} from "lucide-react"
import { debounce, get, isEmpty } from "radash"

import { getRestaurantDetail } from "@/lib/client/api/restaurant"
import { Category, DishItem } from "@/lib/types"
import { RestaurantCart, RestaurantItem } from "@/lib/types/cart"
import { cn, formatCurrency } from "@/lib/utils"
import { MenuItemDetailDialog } from "@/features/resto-detail/components/menu-item-detail-dialog"
import { getToppingDisplay } from "@/features/resto-detail/utils/get-topping-display"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SheetClose, SheetTitle } from "@/components/ui/sheet"
import { Icons } from "@/components/icons"
import { NumberIncrementer } from "@/components/number-incrementer"

import { useCartActions, useCartDetail } from "../hooks/use-cart-detail"
import { CartContentSkeleton } from "./cart-content-skeleton"

// Helper component for empty cart state
function EmptyCart() {
  const router = useRouter()
  const restaurantId = useParams().id

  const { data: restaurant } = useQuery({
    queryKey: ["restaurant", restaurantId],
    queryFn: async () => {
      const res = await getRestaurantDetail(Number(restaurantId))
      return res.data
    },
    enabled: !!restaurantId,
  })

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <Icons.emptyCart
        size={60}
        className="mx-auto text-gray-300 mb-4"
        aria-hidden="true"
      />
      <p className="text-gray-600 mb-8">
        Giỏ hàng{" "}
        {restaurantId
          ? `cho nhà hàng ${get(restaurant, "restaurant_info.name")} `
          : ""}
        hiện đang trống
      </p>

      {restaurantId ? (
        <SheetClose asChild>
          <Button variant="be" className="w-48 rounded-lg">
            Khám phá ngay
          </Button>
        </SheetClose>
      ) : (
        <SheetClose asChild>
          <Button
            variant="be"
            className="w-48 rounded-lg"
            onClick={() => router.push("/")}
          >
            Khám phá ngay
          </Button>
        </SheetClose>
      )}
    </div>
  )
}

// Restaurant card component for the all carts view
function RestaurantCartCard({
  cart,
  isEditing,
  isSelected,
  onSelect,
  onDeletePress,
  onCartPress,
}: {
  cart: RestaurantCart
  isEditing: boolean
  isSelected?: boolean
  onSelect?: (cartId: number) => void
  onCartPress: (cartId: number) => void
  onDeletePress?: (cartId: number) => void
}) {
  const itemCount = cart.restaurant_items.reduce(
    (sum, item) => sum + item.quantity,
    0
  )

  // Determine if restaurant is enabled
  const isEnabled = !cart.is_closed && !cart.is_unavailable

  // Format scheduled time if available
  const scheduledTime =
    cart.scheduled_time_text || cart.scheduled_pick_up_time_text || ""

  const isGroupOrder = !isEmpty(cart.group_id)

  return (
    <div className="group relative">
      <div
        className={cn(
          "flex p-4 hover:bg-gray-50 cursor-pointer relative overflow-hidden",
          !isEnabled && "opacity-70",
          isEditing && "pr-16"
        )}
        onClick={() => onCartPress(cart.cart_id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            onCartPress(cart.cart_id)
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="relative size-16 shrink-0 rounded-md overflow-hidden mr-3">
          {cart.restaurant_image ? (
            <Image
              src={cart.restaurant_image}
              alt={cart.restaurant_name}
              fill
              className="object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <ShoppingBag className="size-6 text-gray-400" />
            </div>
          )}
        </div>

        <div className="flex-1">
          <div className="flex items-center">
            <h3 className="font-medium text-be-text-default line-clamp-2 text-sm">
              {cart.restaurant_name}
            </h3>
            {isGroupOrder && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                Nhóm
              </span>
            )}
          </div>

          <p className="text-xs text-be-text-secondary flex items-center gap-2 mt-1">
            <span className="font-bold">{formatCurrency(cart.sub_total)}</span>
            <span className="text-gray-600">({itemCount} phần)</span>
          </p>

          {scheduledTime && (
            <div className="flex items-center mt-1 text-xs text-gray-500">
              <Clock className="size-3 mr-1" />
              <span>
                {cart.pick_up_enable ? "Lấy tại quán: " : "Giao hàng: "}
                {scheduledTime}
              </span>
            </div>
          )}

          {!isEnabled && (
            <div className="flex items-center mt-1 text-xs text-orange-500">
              <AlertCircle className="size-3 mr-1" />
              <span>
                {cart.is_closed ? "Quán đã đóng cửa" : "Quán tạm ngưng phục vụ"}
              </span>
            </div>
          )}

          {/* Delete button */}
          {!isEditing && onDeletePress && (
            <div className="flex gap-2 mt-2">
              <Button
                variant="ghost"
                className="rounded-lg border border-gray-200 h-auto py-1 w-auto"
                onClick={(e) => {
                  e.stopPropagation()
                  onDeletePress(cart.cart_id)
                }}
              >
                <Trash2 className="size-4" strokeWidth={1.5} />
              </Button>
            </div>
          )}
        </div>

        {isEditing ? (
          <Checkbox
            className="absolute right-4 top-1/2 -translate-y-1/2"
            checked={isSelected}
            onCheckedChange={() => onSelect && onSelect(cart.cart_id)}
          />
        ) : (
          <ChevronRight className="size-5 text-gray-400 ml-2" />
        )}
      </div>
    </div>
  )
}

interface CartContentProps {
  restaurantId?: number
}

export function CartContent({ restaurantId }: CartContentProps) {
  const router = useRouter()

  const [editingItemId, setEditingItemId] = React.useState<number | null>(null)
  const [editingItemIndex, setEditingItemIndex] = React.useState<number | null>(
    null
  )
  const [editingItemData, setEditingItemData] = React.useState<
    Category["items"][number] | null
  >(null)
  const [updatingItemIndex, setUpdatingItemIndex] = React.useState<
    number | null
  >(null)

  const { data: cartResponse, isLoading, isPending, error } = useCartDetail()
  const {
    isEditing,
    selectedCarts,
    deleteDialogOpen,
    cartsToDelete,
    toggleEditMode,
    toggleCartSelection,
    toggleSelectAll,
    confirmDelete,
    executeDelete,
    cancelDelete,
    getCartById,
    updateItemQuantityByIndex,
    clearRestaurantCart,
    clearRestaurantCartMutation,
    updateItemQuantityByIndexMutation,
  } = useCartActions()

  const handleCartPress = (cartId: number) => {
    if (isEditing) {
      toggleCartSelection(cartId)
    } else {
      const cart = getCartById(cartId)
      if (cart) {
        router.push(`/resto/${cart.restaurant_id}`)
      }
    }
  }

  const { data: dishMapData } = useQuery({
    queryKey: ["restaurant", "dishes", restaurantId],
    queryFn: async () => {
      if (!restaurantId) return {}
      const res = await getRestaurantDetail(restaurantId)
      const allDishes = res.data.categories.flatMap(
        (cat: Category) => cat.items
      )
      return allDishes.reduce(
        (acc: Record<number, DishItem>, dish: DishItem) => {
          acc[dish.restaurant_item_id] = dish
          return acc
        },
        {} as Record<number, DishItem>
      )
    },
    enabled: !!restaurantId,
    placeholderData: {},
    staleTime: 10 * 60 * 1000,
  })

  // Handle edit button click for cart item
  const handleEditCartItem = (itemId: number, itemIndex: number) => {
    const selectedCart = restaurantId
      ? cartResponse?.carts.find((cart) => cart.restaurant_id === restaurantId)
      : null

    if (!selectedCart) return

    // Find the dish data from dishMapData
    const dishData = dishMapData?.[itemId] || null
    setEditingItemData(dishData as Category["items"][number] | null)
    setEditingItemId(itemId)
    setEditingItemIndex(itemIndex)
  }

  const getPrice = useCallback(
    (item: RestaurantItem) => {
      const customisePrice = item.customizes?.reduce(
        (sum, cust) =>
          sum +
          (cust.options?.reduce(
            (optSum, opt) => optSum + opt.price * (opt.quantity || 1),
            0
          ) || 0),
        0
      )
      return {
        price: item.price + customisePrice,
        originalPrice:
          (dishMapData?.[item.item_id]?.old_price || 0) + customisePrice,
      }
    },
    [dishMapData]
  )

  const selectedCart = React.useMemo(
    () =>
      restaurantId
        ? cartResponse?.carts.find(
            (cart) => cart.restaurant_id === restaurantId
          )
        : null,
    [cartResponse, restaurantId]
  )

  // --- Rendering Logic ---
  if (isLoading) {
    return <CartContentSkeleton />
  }

  if (
    error ||
    !cartResponse ||
    !cartResponse.carts ||
    cartResponse.carts.length === 0
  ) {
    return <EmptyCart />
  }

  if (restaurantId) {
    const itemsToRender = selectedCart?.restaurant_items || []

    const subTotal = itemsToRender.reduce((total, item) => {
      const itemPriceDetails = getPrice(item)
      return total + itemPriceDetails.price * item.quantity
    }, 0)

    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <SheetTitle className="text-lg font-semibold flex items-center gap-2">
            Giỏ hàng
            <Button
              variant="ghost"
              className="px-1 py-0 text-blue-700 hover:text-blue-800 disabled:text-gray-400"
              onClick={() => {
                if (restaurantId && updatingItemIndex === null) {
                  clearRestaurantCart(restaurantId)
                }
              }}
              disabled={
                !selectedCart ||
                selectedCart.restaurant_items.length === 0 ||
                clearRestaurantCartMutation.isPending ||
                updatingItemIndex !== null
              }
            >
              {clearRestaurantCartMutation.isPending
                ? "Đang xóa..."
                : "Xoá hết"}
            </Button>
          </SheetTitle>
        </div>

        {itemsToRender.length === 0 && <EmptyCart />}

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {itemsToRender.map((item, index) => {
              const currentQuantity = item?.quantity || 0

              return (
                <div
                  key={`${item.item_id}-${item.price}-${getToppingDisplay(item, dishMapData)}-${item.note}-${index}`}
                  className="flex flex-col border-b pb-4 gap-1"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm">
                          {dishMapData?.[item.item_id]?.item_name ||
                            "Đang tải..."}
                        </h3>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation()
                            if (updatingItemIndex === null) {
                              handleEditCartItem(item.item_id, index)
                            }
                          }}
                          disabled={updatingItemIndex !== null}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {getToppingDisplay(item, dishMapData) && (
                        <p className="text-xs text-gray-500 whitespace-pre-wrap">
                          {getToppingDisplay(item, dishMapData)}
                        </p>
                      )}

                      <p className="text-xs text-gray-500 pt-1">
                        <span className="flex items-center gap-1">
                          <Icons.note className="size-4" />{" "}
                          {item.note || "Ghi chú cho quán"}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm">
                        {formatCurrency(getPrice(item).price)}

                        {getPrice(item).originalPrice >
                          getPrice(item).price && (
                          <span className="text-xs line-through font-normal text-gray-500 ml-0.5">
                            {formatCurrency(getPrice(item).originalPrice)}
                          </span>
                        )}
                      </p>
                    </div>
                    <NumberIncrementer
                      value={currentQuantity}
                      disabled={
                        updatingItemIndex === index ||
                        updateItemQuantityByIndexMutation.isPending ||
                        isPending
                      }
                      className="h-8"
                      onDecrement={() => {
                        if (
                          currentQuantity > 0 &&
                          restaurantId &&
                          updatingItemIndex === null
                        ) {
                          const newQuantity = currentQuantity - 1
                          setUpdatingItemIndex(index)
                          try {
                            updateItemQuantityByIndex(
                              restaurantId,
                              index,
                              newQuantity
                            )
                          } catch (err) {
                            console.error("Failed to decrement quantity", err)
                          } finally {
                            setUpdatingItemIndex(null)
                          }
                        }
                      }}
                      onIncrement={() => {
                        if (restaurantId && updatingItemIndex === null) {
                          const newQuantity = currentQuantity + 1
                          setUpdatingItemIndex(index)
                          try {
                            updateItemQuantityByIndex(
                              restaurantId,
                              index,
                              newQuantity
                            )
                          } catch (err) {
                            console.error("Failed to increment quantity", err)
                          } finally {
                            setUpdatingItemIndex(null)
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </ScrollArea>

        <div className="p-4 space-y-3 border-t border-gray-200/50">
          <SheetClose asChild>
            <Button
              variant="be"
              className="w-full h-11 rounded-lg"
              disabled={
                itemsToRender.length === 0 ||
                updatingItemIndex !== null ||
                clearRestaurantCartMutation.isPending ||
                updateItemQuantityByIndexMutation.isPending ||
                isPending
              }
              onClick={() => {
                if (updatingItemIndex === null) {
                  router.push(
                    `/checkout?cart_id=${selectedCart?.cart_id}&flow=normal`
                  )
                }
              }}
            >
              Chuyển đến thanh toán • {formatCurrency(subTotal)}
            </Button>
          </SheetClose>
        </div>

        {/* Item edit dialog */}
        <MenuItemDetailDialog
          selectedItemId={editingItemId}
          selectedItemIndex={editingItemIndex}
          setSelectedItemId={setEditingItemId}
          setSelectedItemIndex={setEditingItemIndex}
          selectedItem={editingItemData}
          isEditing={true}
          restaurantId={String(restaurantId)}
        />
      </div>
    )
  }

  // If no restaurantId is provided, show all restaurant carts
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200/50 flex">
        <div>
          <h2 className="text-lg font-semibold">Giỏ hàng của tôi</h2>
          <p className="text-xs text-gray-500">
            {cartResponse.carts.length} nhà hàng
          </p>
        </div>

        {cartResponse.carts.length > 0 && (
          <Button
            variant="ghost"
            className="text-blue-600 px-2 h-8"
            onClick={toggleEditMode}
          >
            {isEditing ? "Hủy" : "Sửa"}
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="divide-y divide-gray-200/40">
          {cartResponse.carts.map((cart) => (
            <RestaurantCartCard
              key={cart.cart_id}
              cart={cart}
              isEditing={isEditing}
              isSelected={selectedCarts.includes(cart.cart_id)}
              onSelect={toggleCartSelection}
              onCartPress={handleCartPress}
              onDeletePress={
                !isEditing ? (cartId) => confirmDelete([cartId]) : undefined
              }
            />
          ))}
        </div>
      </ScrollArea>

      {isEditing && (
        <div className="p-4 border-t flex justify-between items-center bg-white">
          <Button
            variant="outline"
            className="text-sm"
            onClick={toggleSelectAll}
          >
            {selectedCarts.length === cartResponse.carts.length
              ? "Bỏ chọn tất cả"
              : "Chọn tất cả"}
          </Button>

          <Button
            variant="destructive"
            className="px-4"
            disabled={selectedCarts.length === 0 || deleteDialogOpen}
            onClick={() => {
              if (selectedCarts.length > 0) {
                confirmDelete(selectedCarts)
              }
            }}
          >
            Xóa {selectedCarts.length > 0 ? `(${selectedCarts.length})` : ""}
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={cancelDelete}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Xóa giỏ hàng</DialogTitle>
            <DialogDescription>
              {cartsToDelete.length > 1
                ? `Bạn có chắc chắn muốn xóa ${cartsToDelete.length} giỏ hàng đã chọn?`
                : "Bạn có chắc chắn muốn xóa giỏ hàng này?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-between sm:justify-end">
            <Button type="button" variant="outline" onClick={cancelDelete}>
              Hủy
            </Button>
            <Button type="button" variant="destructive" onClick={executeDelete}>
              Xóa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
